import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useLocation } from "react-router-dom";
import {
  CognitoUserPool,
  AuthenticationDetails,
  CognitoUser
} from "amazon-cognito-identity-js";
import { secureFetch } from "../utils/secureFetch";
import SponsorBanner from "../components/SponsorBanner";
import { trackEvent } from "../utils/analytics";

const userPool = new CognitoUserPool({
  UserPoolId: process.env.REACT_APP_COGNITO_USER_POOL_ID,
  ClientId: process.env.REACT_APP_COGNITO_CLIENT_ID
});

export default function Login() {
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const [showForgot, setShowForgot] = useState(false);
  const [forgotStep, setForgotStep] = useState(1);
  const [resetEmail, setResetEmail] = useState("");
  const [resetCode, setResetCode] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const location = useLocation();
  const params = new URLSearchParams(location.search);
  const sessionExpired = params.get("sessionExpired");

  const handleLogin = (e) => {
    e.preventDefault();
    setLoading(true);
    setError("");
    trackEvent("login_started");

    const user = new CognitoUser({ Username: email, Pool: userPool });
    const authDetails = new AuthenticationDetails({ Username: email, Password: password });

    user.authenticateUser(authDetails, {
      onSuccess: async (authResult) => {
  try {
    // 1) Save tokens
    const idToken = authResult.getIdToken().getJwtToken();
    const accessToken = authResult.getAccessToken().getJwtToken();

    localStorage.setItem("beercard_id_token", idToken);
    localStorage.setItem("beercard_access_token", accessToken);
    localStorage.setItem("beercard_logged_in_email", email);
    localStorage.setItem("beercard_token", idToken);

    // 2) Get groups from token
    const payload = JSON.parse(atob(idToken.split(".")[1]));
    const groups = payload["cognito:groups"] || [];

    // 3) Fetch user profile from backend
    const base = process.env.REACT_APP_API_BASE;
    const user = await secureFetch(`${base}/user`);

    console.log("USER RESPONSE", user);

    if (user?.teamId) {
      localStorage.setItem("beercard_teamId", user.teamId);
    }

    if (user?.clubId) {
      localStorage.setItem("beercard_clubId", user.clubId);
    }

    // 4) Determine role (DB first)
    const dbRole = user?.role;

    let finalRole = "player";

    if (dbRole === "superadmin") {
      finalRole = "superadmin";
    } else if (dbRole === "admin") {
      finalRole = "admin";
    } else if (dbRole === "player") {
      finalRole = "player";
    } else if (groups.includes("admin")) {
      finalRole = "admin";
    } else if (groups.includes("superadmin")) {
      finalRole = "superadmin";
    }

    // 5) Save role, teamId and clubId
    localStorage.setItem("beercard_role", finalRole);
    trackEvent("login_success", { role: finalRole });

    if (user?.teamId) {
      localStorage.setItem("beercard_teamId", user.teamId);
    }

    if (user?.clubId) {
      localStorage.setItem("beercard_clubId", user.clubId);
    }

    // 6) Redirect
    const redirect =
      finalRole === "superadmin"
        ? "/superadmin"
        : finalRole === "admin"
        ? "/admin"
        : "/player";

    navigate(redirect, { replace: true });

  } catch (err) {
    console.error("❌ Login error:", err);
    trackEvent("login_failed", { reason: "profile_error" });
    alert("Login failed. Please try again.");
  }
},

      onFailure: (err) => {
        trackEvent("login_failed", { reason: err.code || "auth_failed" });
        setError(err.message || "Login failed");
        setLoading(false);
      }
    });
  };

  return (
    <div className="min-h-screen bg-gray-100 dark:bg-gray-900 flex flex-col items-center justify-center px-4 py-8">
      <div className="w-full max-w-6xl grid grid-cols-1 lg:grid-cols-[280px_384px_280px] gap-6 items-start justify-center">
        <aside className="hidden lg:flex flex-col gap-4 pt-4">
          <SponsorBanner placement="login_desktop_left" label="Local sponsor" />
          <SponsorBanner placement="login_desktop_left_2" label="TeamKitty partner" compact />
        </aside>

        <main className="w-full">
          <div className="w-full p-6 bg-white dark:bg-gray-800 rounded shadow">
            {sessionExpired && (
              <div className="mb-4 rounded border border-yellow-300 bg-yellow-50 px-3 py-2 text-sm text-yellow-800">
                You’ve been logged out. Please log back in to continue.
              </div>
            )}

            <h1 className="text-2xl font-bold text-center text-gray-900 dark:text-white mb-6">TeamKitty Login</h1>

        {!showForgot && (
          <form onSubmit={handleLogin} className="flex flex-col gap-4">
            <label className="text-sm font-medium text-gray-700 dark:text-gray-300">
              Email
              <input
                type="email"
                className="mt-1 p-2 border border-gray-300 dark:border-gray-600 rounded w-full text-black"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
              />
            </label>

            <label className="text-sm font-medium text-gray-700 dark:text-gray-300">
              Password
              <input
                type="password"
                className="mt-1 p-2 border border-gray-300 dark:border-gray-600 rounded w-full text-black"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
              />
            </label>

            {error && <p className="text-red-600 text-sm">{error}</p>}

            <button
              type="submit"
              disabled={loading}
              className="bg-blue-600 hover:bg-blue-700 text-white py-2 rounded text-sm font-semibold"
            >
              {loading ? "Logging in..." : "Login"}
            </button>
          </form>
        )}

        <p className="text-sm text-center mt-4">
          <button
            onClick={() => setShowForgot(true)}
            className="text-blue-600 hover:underline"
          >
            Forgot Password?
          </button>
        </p>

        {showForgot && (
          <div className="mt-6 p-4 border rounded bg-gray-50 dark:bg-gray-700">
            <h2 className="text-lg font-bold mb-4 text-gray-800 dark:text-white">Reset Password</h2>

            {forgotStep === 1 && (
              <>
                <label className="text-sm font-medium text-gray-700 dark:text-gray-200 block mb-1">
                  Email address
                </label>
                <input
                  type="email"
                  className="p-2 border border-gray-300 dark:border-gray-600 rounded w-full text-black"
                  value={resetEmail}
                  onChange={(e) => setResetEmail(e.target.value)}
                  required
                />
                <button
                  onClick={() => {
                    const user = new CognitoUser({ Username: resetEmail, Pool: userPool });
                    user.forgotPassword({
                      onSuccess: () => {
                        alert("Password reset email sent.");
                        setForgotStep(2);
                      },
                      onFailure: (err) => alert("Error: " + err.message),
                      inputVerificationCode: () => setForgotStep(2)
                    });
                  }}
                  className="mt-3 bg-blue-600 hover:bg-blue-700 text-white py-2 rounded w-full text-sm font-semibold"
                >
                  Send Reset Code
                </button>
              </>
            )}

            {forgotStep === 2 && (
              <>
                <label className="text-sm font-medium text-gray-700 dark:text-gray-200 block mb-1">
                  Verification Code
                </label>
                <input
                  type="text"
                  className="p-2 border border-gray-300 dark:border-gray-600 rounded w-full text-black"
                  value={resetCode}
                  onChange={(e) => setResetCode(e.target.value)}
                  required
                />

                <label className="text-sm font-medium text-gray-700 dark:text-gray-200 block mb-1 mt-4">
                  New Password
                </label>
                <input
                  type="password"
                  className="p-2 border border-gray-300 dark:border-gray-600 rounded w-full text-black"
                  value={newPassword}
                  onChange={(e) => setNewPassword(e.target.value)}
                  required
                />

                <button
                  onClick={() => {
                    const user = new CognitoUser({ Username: resetEmail, Pool: userPool });
                    user.confirmPassword(resetCode, newPassword, {
                      onSuccess: () => {
                        alert("Password reset. You can now log in.");
                        setShowForgot(false);
                        setForgotStep(1);
                        setResetCode("");
                        setNewPassword("");
                        setResetEmail("");
                      },
                      onFailure: (err) => alert("Error: " + err.message)
                    });
                  }}
                  className="mt-4 bg-green-600 hover:bg-green-700 text-white py-2 rounded w-full text-sm font-semibold"
                >
                  Set New Password
                </button>
              </>
            )}

            <button
              onClick={() => {
                setShowForgot(false);
                setForgotStep(1);
                setResetCode("");
                setNewPassword("");
                setResetEmail("");
              }}
              className="text-sm text-gray-600 dark:text-gray-300 hover:underline mt-3 block text-center"
            >
              Cancel
            </button>
          </div>
        )}
          </div>

          <div className="lg:hidden mt-5">
            <SponsorBanner placement="login_mobile" label="Local team sponsor" compact />
          </div>
        </main>

        <aside className="hidden lg:flex flex-col gap-4 pt-4">
          <SponsorBanner placement="login_desktop_right" label="Featured sponsor" />
          <SponsorBanner placement="login_desktop_right_2" label="Community offer" compact />
        </aside>
      </div>

      <div
  style={{
    marginTop: "40px",
    textAlign: "center",
    fontSize: "13px",
    color: "#9ca3af",
  }}
>
  <a href="/privacy" style={{ marginRight: "16px", color: "#9ca3af" }}>
    Privacy Policy
  </a>

  <a href="/terms" style={{ marginRight: "16px", color: "#9ca3af" }}>
    Terms
  </a>

  <a href="/cookies" style={{ color: "#9ca3af" }}>
    Cookies
  </a>
</div>
    </div>
  );
}
